import { AssessmentMethodType, AssessmentWithEntries } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  DataGridTable,
  useCellStates,
  useColumnsWithCellState,
  useGridRefs,
  useNestedState,
} from '@schooly/components/data-grid';
import isEqual from 'lodash.isequal';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { GridContainer } from '../../../components/uikit-components/Grid/Grid';
import { createMarkbookForGroupColumnGrouping } from './common/assessmentColumnGrouping';
import { createMarkbookForGroupColumns } from './common/assessmentColumns';
import {
  getAssessmentId,
  transformMarkbookForGroupData,
  useAssessmentEntryUpdates,
} from './common/assessmentEntryUpdates';
import { useRenderMarkbookForGroupEntryMethodWithMiddleware } from './common/assessmentMethodRendering';
import { middlewareConfig } from './common/assessmentMiddleware';
import { createMarkbookForGroupPermissionChecker } from './common/assessmentPermissions';
import { ASSESSMENT_GRID_SLOT_PROPS } from './common/assessmentStyling';
import { useGradeUpdateHandler } from './common/handlers/gradeHandlers';
import { useScoreUpdateHandler } from './common/handlers/scoreHandlers';
import { MarkbookCellContext } from './common/MarkbookCellContext';
import {
  AssessmentCellState,
  MarkbookForGroupProps,
  MarkbookForGroupRow,
  UpdateAssessmentEntryParams,
} from './common/types';

export interface MarkbookForGroupRef {
  updateEntries: (entries: AssessmentWithEntries[]) => void;
  apiRef: ReturnType<typeof useGridRefs>['apiRef'];
}

export const MarkbookForGroup = forwardRef<MarkbookForGroupRef, MarkbookForGroupProps>(
  (
    {
      groupId,
      assessments: assessmentsInit,
      students,
      lists = {},
      onSuccess,
      styling,
      onApiRefReady,
      showPercentage,
      getAssessmentEntryColor,
      onUpdateError,
    },
    ref,
  ) => {
    const { containerRef, apiRef } = useGridRefs();
    const prevAssessmentsInitRef = useRef(assessmentsInit);

    useEffect(() => {
      onApiRefReady?.(apiRef);
    }, [apiRef, onApiRefReady]);

    const {
      state: assessmentsWithEntries,
      setState: setAssessmentsWithEntries,
      updateState,
    } = useNestedState(assessmentsInit);
    const { permissions } = useAuth();

    const cellContext = useCellStates<AssessmentCellState>();

    useImperativeHandle(
      ref,
      () => ({
        updateEntries: setAssessmentsWithEntries,
        apiRef,
      }),
      [setAssessmentsWithEntries, apiRef],
    );
    const { updateEntry } = useAssessmentEntryUpdates(updateState);

    const updateLocalState = useCallback(
      ({ studentId, methodId, entry, comments, methodType }: UpdateAssessmentEntryParams) => {
        updateEntry({
          studentId,
          assessmentId: getAssessmentId(methodId, assessmentsWithEntries ?? []),
          methodId,
          entry,
          comments,
          groupId,
          methodType,
        });
        onSuccess?.();
      },
      [assessmentsWithEntries, groupId, onSuccess, updateEntry],
    );

    const permissionChecker = createMarkbookForGroupPermissionChecker(
      assessmentsWithEntries ?? [],
      permissions,
    );

    // Create score handler with middleware
    const scoreUpdateHandler = useScoreUpdateHandler({
      cellContext,
      middlewareConfig: middlewareConfig[AssessmentMethodType.Score],
      updateLocalState,
      onUpdateError,
    });

    // Legacy async wrapper for Grade/Comment (they still use the old pattern)
    const updateEntryWithMiddleware = useCallback(
      async (params: UpdateAssessmentEntryParams) => {
        updateLocalState(params);
      },
      [updateLocalState],
    );

    // Keep legacy renderer for Grade/Comment
    const renderAssessmentEntryMethodWithMiddleware =
      useRenderMarkbookForGroupEntryMethodWithMiddleware(
        groupId,
        lists || {},
        updateEntryWithMiddleware,
        cellContext,
        middlewareConfig,
        onSuccess,
      );

    const rows = transformMarkbookForGroupData(students, assessmentsWithEntries ?? []);

    const gradeUpdateHandler = useGradeUpdateHandler({
      cellContext,
      middlewareConfig: middlewareConfig[AssessmentMethodType.Grade],
      updateLocalState: updateEntryWithMiddleware,
      onUpdateError,
    });

    const columns = useColumnsWithCellState(
      createMarkbookForGroupColumns({
        assessments: assessmentsWithEntries ?? [],
        renderAssessmentEntryMethod: renderAssessmentEntryMethodWithMiddleware,
        styling,
        scoreUpdateHandler,
        permissionChecker,
        gradeUpdateHandler,
        lists,
      }),

      cellContext.getCellState,
    );

    const columnGroupingModel = createMarkbookForGroupColumnGrouping({
      assessments: assessmentsWithEntries ?? [],
    });

    const canEdit = useCallback(
      ({ row, field }: { row: MarkbookForGroupRow; field: string }): boolean => {
        const studentId = row.student?.user?.relation_id;
        if (!studentId) return false;
        return permissionChecker.isCellEditable(studentId, field);
      },
      [permissionChecker],
    );

    useEffect(() => {
      if (!isEqual(prevAssessmentsInitRef.current, assessmentsInit)) {
        setAssessmentsWithEntries(assessmentsInit);
        prevAssessmentsInitRef.current = assessmentsInit;
      }
    }, [assessmentsInit, setAssessmentsWithEntries]);

    return (
      <MarkbookCellContext.Provider value={{ showPercentage, getAssessmentEntryColor }}>
        <GridContainer ref={containerRef} sx={styling.gridContainerSx}>
          <DataGridTable
            rows={rows}
            columns={columns}
            isCellEditable={canEdit}
            apiRef={apiRef}
            columnGroupingModel={columnGroupingModel}
            {...styling.dataGridProps}
            sx={styling.dataGridSx}
            slotProps={ASSESSMENT_GRID_SLOT_PROPS}
            slots={slots}
            withDataGridHover
          />
        </GridContainer>
      </MarkbookCellContext.Provider>
    );
  },
);

const slots = { columnHeaders: () => null } as const;
